import PermissionsPickerModal from "../shared/PermissionsPickerModal";
import RolesSelectionModal from "../members/RolesSelectionModal";
import AssignMembersModal, { AssignableMember } from "./AssignMembersModal";
import DeleteRoleModal from "./DeleteRoleModal";
import RoleFormModal from "./RoleFormModal";

type PermissionGroup = {
  id: string;
  label: string;
  permissions: string[];
};

type ReassignmentRole = {
  id: string;
  name: string;
  description: string;
  permissions: string[];
};

type RolesPermissionsModalsProps = {
  isCreateRoleOpen: boolean;
  isEditRoleOpen: boolean;
  isPermissionsOpen: boolean;
  isAssignMembersOpen: boolean;
  isDeleteRoleOpen: boolean;
  isDeleteRolesModalOpen: boolean;
  isCreateRoleFormValid: boolean;
  isEditRoleFormValid: boolean;
  isCreatingRole: boolean;
  isUpdatingRole: boolean;
  isAssigningMembers: boolean;
  isDeletingRole: boolean;
  newRoleName: string;
  newRoleDescription: string;
  selectedPermissions: string[];
  createRoleError: string | null;
  createRoleValidationMessage: string | null;
  updateRoleError: string | null;
  editRoleValidationMessage: string | null;
  permissionQuery: string;
  filteredPermissionGroups: PermissionGroup[];
  isPermissionsLoading: boolean;
  permissionsError: string | null;
  expandedGroupId: string;
  activeRoleName: string;
  availableMembersForActiveRole: AssignableMember[];
  selectedMemberIdsForRoleAssign: string[];
  roleAssignMemberQuery: string;
  assignMembersError: string | null;
  rolePendingDeletionName: string;
  membersNeedingReassignment: number;
  selectedReassignmentRoleName: string;
  canChooseReassignment: boolean;
  deleteRoleError: string | null;
  reassignmentRoleOptionsForModal: ReassignmentRole[];
  onCreateRoleOpenChange: (open: boolean) => void;
  onCreateRoleNameChange: (value: string) => void;
  onCreateRoleDescriptionChange: (value: string) => void;
  onOpenPermissions: () => void;
  onCreateRoleSubmit: () => void;
  onCreateRoleCancel: () => void;
  onEditRoleOpenChange: (open: boolean) => void;
  onEditRoleSubmit: () => void;
  onEditRoleCancel: () => void;
  onPermissionsOpenChange: (open: boolean) => void;
  onPermissionQueryChange: (value: string) => void;
  onExpandedGroupIdChange: (value: string) => void;
  isPermissionSelected: (permission: string) => boolean;
  onTogglePermission: (permission: string) => void;
  onToggleGroupPermissions: (groupPermissions: string[]) => void;
  onAssignMembersOpenChange: (open: boolean) => void;
  onRoleAssignMemberQueryChange: (value: string) => void;
  onToggleMemberSelection: (memberId: string) => void;
  onAssignMembers: () => void;
  onAssignMembersCancel: () => void;
  onDeleteRoleOpenChange: (open: boolean) => void;
  onOpenReassignmentRolesModal: () => void;
  onDeleteRoleCancel: () => void;
  onDeleteRole: () => void;
  onDeleteRolesSelectionOpenChange: (open: boolean) => void;
  onToggleDeleteReassignmentRole: (roleName: string) => void;
};

const RolesPermissionsModals = ({
  isCreateRoleOpen,
  isEditRoleOpen,
  isPermissionsOpen,
  isAssignMembersOpen,
  isDeleteRoleOpen,
  isDeleteRolesModalOpen,
  isCreateRoleFormValid,
  isEditRoleFormValid,
  isCreatingRole,
  isUpdatingRole,
  isAssigningMembers,
  isDeletingRole,
  newRoleName,
  newRoleDescription,
  selectedPermissions,
  createRoleError,
  createRoleValidationMessage,
  updateRoleError,
  editRoleValidationMessage,
  permissionQuery,
  filteredPermissionGroups,
  isPermissionsLoading,
  permissionsError,
  expandedGroupId,
  activeRoleName,
  availableMembersForActiveRole,
  selectedMemberIdsForRoleAssign,
  roleAssignMemberQuery,
  assignMembersError,
  rolePendingDeletionName,
  membersNeedingReassignment,
  selectedReassignmentRoleName,
  canChooseReassignment,
  deleteRoleError,
  reassignmentRoleOptionsForModal,
  onCreateRoleOpenChange,
  onCreateRoleNameChange,
  onCreateRoleDescriptionChange,
  onOpenPermissions,
  onCreateRoleSubmit,
  onCreateRoleCancel,
  onEditRoleOpenChange,
  onEditRoleSubmit,
  onEditRoleCancel,
  onPermissionsOpenChange,
  onPermissionQueryChange,
  onExpandedGroupIdChange,
  isPermissionSelected,
  onTogglePermission,
  onToggleGroupPermissions,
  onAssignMembersOpenChange,
  onRoleAssignMemberQueryChange,
  onToggleMemberSelection,
  onAssignMembers,
  onAssignMembersCancel,
  onDeleteRoleOpenChange,
  onOpenReassignmentRolesModal,
  onDeleteRoleCancel,
  onDeleteRole,
  onDeleteRolesSelectionOpenChange,
  onToggleDeleteReassignmentRole,
}: RolesPermissionsModalsProps) => {
  return (
    <>
      <RoleFormModal
        open={isCreateRoleOpen}
        onOpenChange={onCreateRoleOpenChange}
        title="Create new role"
        submitLabel="Create Role"
        roleName={newRoleName}
        onRoleNameChange={onCreateRoleNameChange}
        roleDescription={newRoleDescription}
        onRoleDescriptionChange={onCreateRoleDescriptionChange}
        selectedPermissions={selectedPermissions}
        onOpenPermissions={onOpenPermissions}
        onSubmit={onCreateRoleSubmit}
        onCancel={onCreateRoleCancel}
        submitDisabled={!isCreateRoleFormValid || isCreatingRole}
        isSubmitting={isCreatingRole}
        footerMessage={createRoleError ?? createRoleValidationMessage}
        messageClassName={createRoleError ? "text-[#C24E4E]" : "text-[#6B6F8A]"}
      />

      <RoleFormModal
        open={isEditRoleOpen}
        onOpenChange={onEditRoleOpenChange}
        title="Edit role & permissions"
        submitLabel="Save changes"
        roleName={newRoleName}
        onRoleNameChange={onCreateRoleNameChange}
        roleDescription={newRoleDescription}
        onRoleDescriptionChange={onCreateRoleDescriptionChange}
        selectedPermissions={selectedPermissions}
        onOpenPermissions={onOpenPermissions}
        onSubmit={onEditRoleSubmit}
        onCancel={onEditRoleCancel}
        submitDisabled={!isEditRoleFormValid || isUpdatingRole}
        isSubmitting={isUpdatingRole}
        footerMessage={updateRoleError ?? editRoleValidationMessage}
        messageClassName={updateRoleError ? "text-[#C24E4E]" : "text-[#6B6F8A]"}
      />

      <PermissionsPickerModal
        open={isPermissionsOpen}
        onOpenChange={onPermissionsOpenChange}
        searchQuery={permissionQuery}
        onSearchQueryChange={onPermissionQueryChange}
        groups={filteredPermissionGroups}
        isLoading={isPermissionsLoading}
        error={permissionsError}
        expandedGroupId={expandedGroupId}
        onExpandedGroupIdChange={onExpandedGroupIdChange}
        isPermissionSelected={isPermissionSelected}
        onTogglePermission={onTogglePermission}
        onToggleGroupPermissions={onToggleGroupPermissions}
        selectedCount={selectedPermissions.length}
      />

      <AssignMembersModal
        open={isAssignMembersOpen}
        onOpenChange={onAssignMembersOpenChange}
        roleName={activeRoleName}
        members={availableMembersForActiveRole}
        selectedMemberIds={selectedMemberIdsForRoleAssign}
        memberQuery={roleAssignMemberQuery}
        onMemberQueryChange={onRoleAssignMemberQueryChange}
        onToggleMember={onToggleMemberSelection}
        onAssign={onAssignMembers}
        onCancel={onAssignMembersCancel}
        isAssigning={isAssigningMembers}
        error={assignMembersError}
      />

      <DeleteRoleModal
        open={isDeleteRoleOpen}
        onOpenChange={onDeleteRoleOpenChange}
        roleName={rolePendingDeletionName}
        membersNeedingReassignment={membersNeedingReassignment}
        reassignmentRoleName={selectedReassignmentRoleName}
        canChooseReassignment={canChooseReassignment}
        onOpenReassignmentRolesModal={onOpenReassignmentRolesModal}
        onCancel={onDeleteRoleCancel}
        onDelete={onDeleteRole}
        isDeleting={isDeletingRole}
        error={deleteRoleError}
      />

      <RolesSelectionModal
        open={isDeleteRolesModalOpen}
        onOpenChange={onDeleteRolesSelectionOpenChange}
        roles={reassignmentRoleOptionsForModal}
        selectedRoles={selectedReassignmentRoleName ? [selectedReassignmentRoleName] : []}
        onToggleRoleSelection={onToggleDeleteReassignmentRole}
        className="z-[60]"
      />
    </>
  );
};

export default RolesPermissionsModals;
